// Copyright 2019 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { EventAggregator } from "aurelia-event-aggregator";
import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    ComponentBind,
    ComponentUnbind,
    computedFrom,
    containerless,
    customElement,
    Disposable,
    Loader,
    PLATFORM,
} from "aurelia-framework";
import { Compose } from "aurelia-templating-resources";
import { IWorkflowModalConfig, WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { HelpWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/HelpWorkflowModuleCatalog";
import { TimelineEntryType } from "datatypes/SoramaWebApi";
import { PromiseExtensions } from "extensions/PromiseExtensions";
import { ErrorDialogModel } from "modules/dialogs/Error/ErrorDialog";
import { ImageExportState } from "modules/modals/ImageExport/ImageExportModal";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ProjectRepository } from "repositories/ProjectRepository";
import { TimelineEntriesRepository } from "repositories/TimelineEntriesRepository";
import { ExportService } from "services/Export/ExportService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { UserSettingsService } from "services/UserManagement/UserSettingsService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleModalCreator,
    IWorkflowModuleNotification,
    IWorkflowModuleNotificationInstance,
    IWorkflowModuleReference,
} from "../Interfaces/IWorkflowModule";
import { IWorkflowModuleConfig } from "../Interfaces/IWorkflowModuleConfig";
import { IWorkflowModuleController, IWorkflowRouter } from "../Interfaces/IWorkflowRouter";
import { SidebarRouter } from "../Sidebar/SidebarRouter";
import { WorkflowModuleModalCreator } from "../WorkflowModal/WorkflowModuleModalCreator";
import { WorkflowProperty, WorkflowPropertyManager } from "../WorkflowProperties/WorkflowPropertyObserverManager";
import { WorkflowRouter } from "../WorkflowRouter/WorkflowRouter";
import { IWorkflowModuleExportVisual, IWorkflowModuleTimelineEvent } from "./../Interfaces/IWorkflowModule";
import { WorkflowModuleNotification, WorkflowModuleNotificationInstance } from "./WorkflowModuleNotification";
import { WorkflowModuleRequirements } from "./WorkflowModuleRequirements";
import { WorkflowModuleTimelineEventFactory } from "./WorkflowModuleTimeEvent";

export class ModuleHeaderMode {
    public static workflow = "workflow";
    public static sidebar = "sidebar";
    public static none = "none";
}

@customElement("workflow-module-instance")
@containerless()
@autoinject
export class WorkflowModuleInstance implements IWorkflowModuleInstance, ComponentBind, ComponentUnbind {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public moduleController: IWorkflowModuleController;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public workflowPropertyManager: WorkflowPropertyManager;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public useHeader: ModuleHeaderMode = ModuleHeaderMode.workflow;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public workflowRouter: IWorkflowRouter;
    public moduleReference: IWorkflowModuleReference;

    private moduleCompose: Compose;

    private modalCreator: IWorkflowModuleModalCreator<unknown, unknown>;

    private notificationInstance: IWorkflowModuleNotificationInstance;

    private observerSubscriptions: Disposable[];
    private instanceLifetimePromise: Promise<void>;

    private lock = false;
    private moduleIsBeingDisposed = false;

    protected highlightCheckmarkIcon: boolean;

    protected isFullScreen = false;

    private titleOverride = null;

    @bindable
    public fullScreenElement: Element;

    @computedFrom("moduleController")
    public get moduleId(): number {
        if (!this.moduleController) return undefined;

        return this.moduleController.moduleId;
    }

    @computedFrom("moduleController")
    protected get description(): IWorkflowModuleConfig {
        if (!this.moduleController) return undefined;

        return this.moduleController.config;
    }

    @computedFrom("moduleController")
    public get variant(): string {
        if (!this.moduleController) return "";
        return this.moduleController.variant;
    }

    public get title(): string {
        return this.titleOverride ?? this.moduleController?.config?.title ?? " ";
    }

    public set title(value: string) {
        this.titleOverride = value;
    }

    public constructor(
        private readonly loader: Loader,
        private readonly bindingEngine: BindingEngine,
        private readonly userSettingsService: UserSettingsService,
        private readonly moduleRequirements: WorkflowModuleRequirements,
        private readonly shellviewManager: ShellViewManager,
        private readonly projectRepository: ProjectRepository,
        private readonly timelineEventRepository: TimelineEntriesRepository,
        private readonly projectManagementService: IProjectManagementService,
        private readonly exportService: ExportService,
        private eventAggregator: EventAggregator,
        private readonly workflowModeService: WorkflowModeService,
        private readonly dialogService: DialogService
    ) {}

    public bind(): void {
        this.moduleIsBeingDisposed = false;
        this.observerSubscriptions = [];
        this.moduleRequirements.subscribeModule(this.description);

        this.instanceLifetimePromise = new Promise((resolve, reject) => {
            // one shot observing
            const composeObserver = this.bindingEngine
                .propertyObserver(this.moduleCompose, "currentViewModel")
                .subscribe((x) => {
                    this.moduleReference = x;
                    (<unknown>this.moduleReference)["moduleId"] = this.moduleController.moduleId;
                    (<unknown>this.moduleReference)["moduleName"] = this.description.name;

                    // Check for user settings
                    this.userSettingsService
                        .bindUserSettingsProperty(this.moduleReference)
                        .then((x) => this.observerSubscriptions.push(x))
                        .then(() => {
                            if (this.moduleReference) {
                                const promise = this.workflowPropertyManager
                                    ? this.workflowPropertyManager.watchModule(this.moduleReference)
                                    : Promise.resolve();

                                // If this module has a timeline button, uncheck this when workflow properties change
                                if (
                                    this.workflowPropertyManager != undefined &&
                                    this.moduleController.config.timelineEventType
                                ) {
                                    this.workflowPropertyManager.subscribeWorkflowPropertyChanged(() => {
                                        this.highlightCheckmarkIcon = false;
                                    });
                                }

                                return promise.then(() => {
                                    if (this.moduleReference.activateModule) {
                                        this.moduleController.moduleInstance = this;
                                        return PromiseExtensions.catchAllErrors(() =>
                                            this.moduleReference.activateModule(this)
                                        ).catch((e) => {
                                            console.warn(e);
                                        });
                                    }
                                });
                            }
                        })
                        .then(() => {
                            composeObserver.dispose();

                            if (this.workflowRouter) this.workflowRouter.scrollIntoView(this.moduleController.moduleId);

                            resolve();
                        });
                });
        });

        // Store expanded state in cache
        this.userSettingsService.bind(this.description.name, "expanded", this).then((x) => {
            this.observerSubscriptions.push(x);
        });

        // Store collapsed state in cache
        this.userSettingsService.bind(this.description.name, "moduleCollapsed", this).then((x) => {
            this.observerSubscriptions.push(x);
        });
    }

    public unbind(): void {
        this.moduleRequirements.unsubscribeModule();

        this.closeModule();
    }

    public getWorkflow(): IWorkflowRouter {
        return this.workflowRouter;
    }

    public getWorkflowProperties(): WorkflowProperty[] {
        if (this.moduleReference) {
            // Return all workflow properties that this module can use
            return this.workflowPropertyManager.workflowProperties.filter(
                (x) =>
                    (x.prefix == undefined || x.prefix == WorkflowRouter.Prefix) &&
                    (!x.scopeIdxs || x.scopeIdxs.find((y) => y == this.moduleReference.moduleId))
            );
        }

        return [];
    }

    public setWorkflowProperties(properties: WorkflowProperty[]): Promise<void> {
        if (this.moduleReference) {
            return this.workflowPropertyManager.restoreState(this.moduleReference, properties, false);
        }

        return Promise.resolve();
    }

    public restoreModuleState(timelineEntry: IWorkflowModuleTimelineEvent, standalone?: boolean): Promise<void> {
        if (this.moduleReference) {
            return this.workflowPropertyManager
                .restoreState(this.moduleReference, timelineEntry.getWorkflowProperties(), standalone)
                .then(() => {
                    this.highlightCheckmarkIcon = true;
                });
        }

        return Promise.resolve();
    }

    public getModalConfig(): IWorkflowModalConfig {
        if (!this.modalCreator) return undefined;

        return this.modalCreator.modalConfig;
    }

    public showHelp(articleName?: string): void {
        if (this.moduleController.config.help || articleName) {
            this.shellviewManager.openRightSideBar(HelpWorkflowModuleCatalog.HelpModule, [
                {
                    prefix: SidebarRouter.prefix,
                    name: "topicName",
                    value: articleName ? articleName : this.moduleController.config.help.name,
                },
            ]);
        }
    }

    public closeModule(): Promise<void> {
        if (this.moduleIsBeingDisposed) {
            // Already in progress
            return this.instanceLifetimePromise;
        }

        let promise = this.instanceLifetimePromise;
        this.moduleIsBeingDisposed = true;

        return promise.then(() => {
            this.cancelModal();

            if (this.moduleReference && this.moduleReference.deactivateModule) {
                promise = PromiseExtensions.catchAllErrors(() => {
                    this.moduleReference.deactivateModule();
                }).catch((e) => {
                    console.warn(e);
                });
            }

            return promise.then(() => {
                if (this.workflowPropertyManager) {
                    this.workflowPropertyManager.removeModule(this.moduleReference);
                }

                if (this.observerSubscriptions) {
                    for (const sub of this.observerSubscriptions) {
                        sub.dispose();
                    }
                }
            });
        });
    }

    public createModal<T = void, Y = void>(modalConfig: IWorkflowModalConfig): IWorkflowModuleModalCreator<T, Y> {
        return new WorkflowModuleModalCreator<T, Y>(modalConfig, this, (modal: IWorkflowModuleModalCreator<T, Y>) => {
            let resolvePromise: (value: void | PromiseLike<void>) => void;
            const cancelPromise = new Promise<void>((resolve, reject) => {
                resolvePromise = resolve;
            });

            if (this.modalCreator) {
                this.modalCreator.whenCanceled(() => {
                    resolvePromise();
                });
            } else {
                resolvePromise();
            }

            this.cancelModal();

            cancelPromise.then(() => {
                this.modalCreator = modal;
            });
        })
            .whenShowed(() => {
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                }
                this.moduleController.makeActive();
            })
            .whenCanceled(() => {
                this.cancelModal();
            })
            .whenClosed(() => {
                this.cancelModal();
            });
    }

    public lockModule(lock = true): void {
        this.lock = lock;
    }

    public dispatch<T>(action: () => T | Promise<T>): Promise<T> {
        const promise = this.instanceLifetimePromise.then(() => {
            if (this.moduleIsBeingDisposed) {
                return Promise.reject<T>(undefined);
            }

            return action();
        });

        this.instanceLifetimePromise = promise.catch(() => {}).then(() => {});

        return promise;
    }

    public createNotification(): IWorkflowModuleNotification {
        return new WorkflowModuleNotification((notification) => {
            this.lock = notification.lock;
            return (this.notificationInstance = new WorkflowModuleNotificationInstance(notification));
        }).whenClosed(() => {
            this.notificationInstance = undefined;
            this.lock = false;
        });
    }

    // Highlight timeline event (bookmark)
    protected highlightTimelineEvent(): void {
        if (!this.highlightCheckmarkIcon) {
            // If not bookmarked, bookmark
            this.highlightCheckmarkIcon = true; // Instantly show bookmark in the GUI

            this.createTimelineEvent()
                .then((x) => {
                    return x.updateSnapshotFromModule(this).then(() => {
                        return x.setHighlightEvent(true).then(() => {
                            // Here we actually bookmarked the module
                            this.eventAggregator.publish(
                                "moduleHighlighted",
                                this.projectManagementService.currentProject
                            );
                        });
                    });
                })
                .catch((x) => {
                    // If we can't safe the bookmark. Unbookmark and show an error.
                    this.highlightCheckmarkIcon = false;

                    this.dialogService.open({
                        viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                        model: <ErrorDialogModel>{
                            title: "Failed to create bookmark.",
                            text: "Try again.",
                        },
                    });
                });
        }
    }

    public createTimelineEvent(eventType?: TimelineEntryType): Promise<IWorkflowModuleTimelineEvent> {
        if (eventType == undefined && this.moduleController.config.timelineEventType == undefined) {
            return Promise.reject("Event type is unknown. Please define the event type.");
        }

        return new WorkflowModuleTimelineEventFactory(
            this.projectRepository,
            this.timelineEventRepository,
            this.projectManagementService,
            this.exportService
        )
            .forProject(this.projectManagementService.currentProject.item.id)
            .withProperties(this.getWorkflowProperties())
            .ofType(eventType != undefined ? eventType : this.moduleController.config.timelineEventType)
            .register();
    }

    public cancelModal(): void {
        this.modalCreator = undefined;
    }

    public clickOnModule(event: Event): boolean {
        this.moduleController.makeActive();
        return true;
    }

    public getExportImageState(): ImageExportState {
        if (this.moduleReference == undefined) {
            return undefined;
        }

        const modRef = this.moduleReference as IWorkflowModuleExportVisual;

        if (modRef.exportVisual) {
            return modRef.exportVisual();
        }

        return undefined;
    }

    protected moduleControllerChanged(): void {
        this.unbind();
        this.instanceLifetimePromise.then(() => {
            if (!this.moduleIsBeingDisposed) this.bind();
        });
    }

    protected openExportVisualModal(): void {
        const exportState = this.getExportImageState();

        if (exportState != undefined) {
            this.createModal<ImageExportState>(WorkflowModalCatalog.ImageExportModal).withState(exportState).show();
        }
    }

    public toggleFullScreen(): void {
        if (this.isFullScreen) {
            this.isFullScreen = false;
            document.exitFullscreen();
        } else if (this.fullScreenElement) {
            this.isFullScreen = true;
            this.fullScreenElement.requestFullscreen();
        }
    }
}
